import { MapPin, MessageCircle, Phone } from "lucide-react";
import { useState } from "react";

import Link from "@/components/Link";
import { useChat } from "@/contexts/ChatContext";
import type { ChatStoreCard } from "@/lib/chatWidgetUrl";

import {
  STORE_CARD_CALL_LABEL,
  STORE_CARD_MAPS_LABEL,
  STORE_CARD_REVIEWS_SUFFIX,
  STORE_CARD_WHATSAPP_LABEL,
} from "./chatCopy";

/**
 * The `stores` block's card row (D-08, WEB-03).
 *
 * IT RENDERS THE ROW ONLY. The scrolling container is `ChatBlockRenderer`'s `BlockSlot`, which owns
 * the 366 by 320 geometry, the `overflow-x-auto`, the `overscroll-behavior-x: contain` that stops a
 * horizontal drag chaining to the page behind the panel, and the `scroll-snap-type` this component's
 * children snap against. Splitting it that way is what keeps the settled slot geometry in one file.
 *
 * D-08: NATIVE CSS SCROLL SNAP, NO CAROUSEL DEPENDENCY. The vendored embla component in
 * `components/ui` has zero usages anywhere in the app, and a 366px RTL slot would be that library's
 * first outing in the hardest layout direction. `scroll-snap-align` is three declarations and the
 * browser already implements RTL for it.
 *
 * NOTHING HERE MAY THROW. A render failure inside the message list blanks the whole panel (T-38-87).
 * `toSafeCards` has already made every field of every card safe before this component sees it, and
 * nothing below indexes an array unguarded, asserts non-null, or constructs a URL.
 *
 * EVERY CARD STRING RENDERS AS REACT CHILDREN (T-39-29). Titles and subtitles originate in scraped
 * store records, so they are attacker-influenceable text rendering inside the site's own pages, and
 * React's escaping is the mitigation. The raw-HTML escape hatch appears nowhere in this shell.
 */

/**
 * The smallest review count a card will show a rating for (D-06).
 *
 * Three, because: three is the smallest count that reads as more than one person's opinion; a row of
 * cards each saying "0 تقييم" makes a reviews platform look empty at the exact moment the bot is
 * recommending; and a single five-star review visually outranking a forty-review 4.6 is a ranking the
 * data cannot support (T-39-34). Below the threshold the card shows its subtitle instead, which is a
 * category or a location — a real fact rather than a weak claim.
 */
const MIN_REVIEWS_FOR_RATING = 3;

/** The `/store/` route prefix, used only to recover the slug for analytics. Never to build a URL. */
const STORE_PATH_PREFIX = "/store/";

/**
 * 14/600/1.6 for the title and 13/400/1.5 for the secondary line. Both sizes and both weights are
 * values the shell already declares (the contract's "exactly 4 sizes, exactly 2 weights"); a card
 * may not invent a fifth size or a third weight. The title carries the 600 because a card is a
 * navigation target, not running prose — at 400 the whole card read as one flat paragraph
 * (39-08 checkpoint feedback).
 */
const CARD_TITLE_STYLE = {
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.6,
  color: "var(--ys-ink)",
} as const;

const CARD_META_STYLE = {
  fontSize: 13,
  fontWeight: 400,
  lineHeight: 1.5,
  color: "var(--ys-ink-2)",
} as const;

/** The fallback initial, sized up to the 20px house display size inside the brand-tint circle. */
const CARD_INITIAL_STYLE = {
  fontSize: 20,
  fontWeight: 600,
  lineHeight: 1,
  color: "var(--ys-orange-700)",
} as const;

/**
 * One contact icon link (39-08). 44px square — the same touch-target floor the chips carry — with
 * the house focus ring. These are SIBLINGS of the card's main `Link`, never children: an anchor
 * inside an anchor is invalid HTML and browsers split it unpredictably mid-tree.
 */
const CONTACT_ICON_CLASS =
  "flex h-11 w-11 items-center justify-center rounded-[var(--ys-r-sm)] text-[var(--ys-ink-2)] transition-colors hover:bg-[var(--ys-primary-soft)] hover:text-[var(--ys-orange-600)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ys-ink-2)] focus-visible:ring-offset-2";

/**
 * The slug for `chat_store_card_clicked`, recovered from the href the card already carries.
 *
 * Deliberately NOT a new `slug` field on the wire shape: `href` is already the validated same-origin
 * path `safeWidgetHref` produced, so the slug is derivable, and a second field would be a second
 * thing the server could get wrong. Returns the whole path for any route that is not `/store/x`,
 * which keeps the property present and honest rather than empty.
 */
const slugFromHref = (href: string): string =>
  href.startsWith(STORE_PATH_PREFIX) ? href.slice(STORE_PATH_PREFIX.length) : href;

/** The first grapheme of the store name, for the logo fallback box. Never throws on an empty title. */
const initialOf = (title: string): string => Array.from(title.trim())[0] ?? "";

export default function ChatStoreCarousel({ cards }: { cards: ChatStoreCard[] }) {
  const { noteStoreCardClicked, noteStoreContactClicked } = useChat();

  /**
   * `failedLogoIds`, the failed-image set, owned ONCE by the carousel rather than per card.
   *
   * A `useState` inside the `cards.map()` callback below would break the rules of hooks outright —
   * the card count changes turn to turn, so the hook count would change between renders — and
   * `eslint-plugin-react-hooks` fails the build on it. One `Set` keyed by `storeId` (a guaranteed
   * non-empty unique string on `ChatStoreCard`) needs no second component to hold it.
   *
   * The updater always builds a NEW `Set`. Mutating the previous one in place leaves the reference
   * unchanged and React would not re-render, so the broken image would stay broken on screen.
   */
  const [failedLogoIds, setFailedLogoIds] = useState<ReadonlySet<string>>(() => new Set());

  const cardCount = cards.length;

  return (
    // THE ROW NEVER PROGRAMMATICALLY SETS ITS SCROLL POSITION, ON MOUNT OR OTHERWISE, AND THAT IS
    // THE FIX RATHER THAN THE OMISSION. Under the panel's inherited `dir="rtl"`, a scroll offset of
    // zero in the scrolling ancestor ALREADY IS the inline start (the right edge). Assigning one "to
    // reset the carousel", the most tempting wrong move in this component, would scroll the row to
    // its END on the first paint, so the visitor would land on the last recommendation instead of
    // the first. This is `ChatBlockRenderer.tsx`'s explicit warning, restated where it would be
    // violated.
    <div className="flex gap-3">
      {cards.map((card, index) => {
        const showRating =
          typeof card.rating === "number" &&
          typeof card.reviewCount === "number" &&
          card.reviewCount >= MIN_REVIEWS_FOR_RATING;
        const showLogo = card.logoSrc !== undefined && !failedLogoIds.has(card.storeId);

        return (
          <div
            key={card.storeId}
            /*
             * 200px against the slot's roughly 334px content box means one and a half cards are
             * visible, and that half-card peek is what says "swipe" without a control to design,
             * position and localise. `scroll-snap-align: start` puts each card at the inline start
             * of the slot as it settles.
             *
             * NO CARD EVER TAKES FOCUS ON MOUNT, and React's auto-focus prop appears nowhere in
             * this file: a block that steals focus as it streams in takes the keyboard away from a
             * visitor mid-sentence.
             */
            /*
             * `--ys-paper` on the slot's `--ys-bg-2`, plus the house shadow-2, is what makes the
             * card read as a raised, tappable object rather than a bordered rectangle of prose
             * (39-08 checkpoint feedback). Hover trades the shadow up one step and warms the
             * border; no background wash, so the logo band keeps its contrast.
             */
            style={{ scrollSnapAlign: "start" }}
            className="flex w-[200px] shrink-0 flex-col rounded-[var(--ys-r-md)] border border-[var(--ys-rule)] bg-[var(--ys-paper)] shadow-[var(--ys-shadow-2)] transition-all duration-150 hover:-translate-y-0.5 hover:border-[var(--ys-orange-300)] hover:shadow-[var(--ys-shadow-3)]"
          >
            {/* The card's main tap target. `flex-1` so every card's icon row sits on the same
                bottom line however short its title is. */}
            <Link
              to={card.href}
              className="flex min-h-11 flex-1 flex-col gap-2 rounded-[var(--ys-r-md)] p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ys-ink-2)] focus-visible:ring-offset-2"
              onClick={() =>
                // The capture lives in ChatContext's own handler, per CLAUDE.md's shared-primitive
                // rule and the event map's panel-wide rule. This component never calls `posthog` and
                // never reaches for a `track` helper directly.
                noteStoreCardClicked({
                  store_slug: slugFromHref(card.href),
                  position: index,
                  card_count: cardCount,
                })
              }
            >
              {/*
                ROW 1 (39-08 checkpoint): the 56px logo beside the rating stacked over its review
                count, so the card leads with the two facts a chooser actually compares. Thin-data
                cards (D-06) put the subtitle in that slot instead — a category or a location is a
                real fact where a 1-review score is a weak claim. The logo box is what
                `CARD_LOGO_TRANSFORM` is sized for (w-112 = 2x for retina), decorative in both
                variants: the store name is inside the same anchor, so the accessible name is the
                store name and a screen reader hears a list of stores rather than a list of "link".
              */}
              <span className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-[var(--ys-r-sm)] bg-[var(--ys-bg-2)]"
                >
                  {showLogo ? (
                    <img
                      src={card.logoSrc}
                      alt=""
                      loading="lazy"
                      className="h-14 w-14 object-contain"
                      onError={() =>
                        setFailedLogoIds((previous) => new Set(previous).add(card.storeId))
                      }
                    />
                  ) : (
                    /*
                      The initials fallback, on the brand tint with the deep orange the shell
                      already allows as a foreground. Written here rather than reused because this
                      codebase has no initials-avatar component, and `StoreCard.tsx`'s
                      `/placeholder.svg` is a legacy Tailwind-palette component predating the chat
                      shell's tokens.
                    */
                    <span
                      className="flex h-14 w-14 items-center justify-center bg-[var(--ys-primary-tint)]"
                      style={CARD_INITIAL_STYLE}
                    >
                      {initialOf(card.title)}
                    </span>
                  )}
                </span>

                {/* EITHER the rating stack OR the subtitle, never both (D-06). */}
                {showRating ? (
                  <span className="flex min-w-0 flex-col">
                    <span className="flex items-center gap-1" style={CARD_TITLE_STYLE}>
                      {/*
                        `--ys-star` is the house star gold the rest of the site rates with; using
                        it here is what makes a chat card's rating read as the same fact as a store
                        page's. The score carries the title style (14/600) because the number is
                        the datum; the count below it is its 13/400 context.
                      */}
                      <span aria-hidden="true" style={{ color: "var(--ys-star)" }}>
                        ★
                      </span>
                      {card.rating?.toFixed(1)}
                    </span>
                    <span style={CARD_META_STYLE}>
                      {card.reviewCount} {STORE_CARD_REVIEWS_SUFFIX}
                    </span>
                  </span>
                ) : card.subtitle === undefined ? null : (
                  <span className="line-clamp-2 break-words" style={CARD_META_STYLE}>
                    {card.subtitle}
                  </span>
                )}
              </span>

              <span className="line-clamp-2 break-words" style={CARD_TITLE_STYLE}>
                {card.title}
              </span>
            </Link>

            {/*
              ROW 2 (39-08 checkpoint): the contact icons, rendered only for the hrefs that
              survived `toSafeCards`, icons only per the product direction. Each is its own tab
              stop with the whole action as its accessible name. A card whose store carries no
              contact data gets no row at all rather than an empty rule line.
            */}
            {(card.telHref !== undefined ||
              card.whatsappHref !== undefined ||
              card.mapsHref !== undefined) && (
              <span className="mt-auto flex items-center gap-1 border-t border-[var(--ys-rule)] px-2 py-1">
                {card.telHref !== undefined && (
                  <a
                    href={card.telHref}
                    aria-label={STORE_CARD_CALL_LABEL}
                    className={CONTACT_ICON_CLASS}
                    onClick={() =>
                      noteStoreContactClicked({
                        store_slug: slugFromHref(card.href),
                        contact_kind: "phone",
                        position: index,
                      })
                    }
                  >
                    <Phone size={18} aria-hidden="true" />
                  </a>
                )}
                {card.whatsappHref !== undefined && (
                  <a
                    href={card.whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={STORE_CARD_WHATSAPP_LABEL}
                    className={CONTACT_ICON_CLASS}
                    onClick={() =>
                      noteStoreContactClicked({
                        store_slug: slugFromHref(card.href),
                        contact_kind: "whatsapp",
                        position: index,
                      })
                    }
                  >
                    <MessageCircle size={18} aria-hidden="true" />
                  </a>
                )}
                {card.mapsHref !== undefined && (
                  <a
                    href={card.mapsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={STORE_CARD_MAPS_LABEL}
                    className={CONTACT_ICON_CLASS}
                    onClick={() =>
                      noteStoreContactClicked({
                        store_slug: slugFromHref(card.href),
                        contact_kind: "maps",
                        position: index,
                      })
                    }
                  >
                    <MapPin size={18} aria-hidden="true" />
                  </a>
                )}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
